// Smooth-scroll to an element, temporarily disabling document scroll-snap.
// Chrome cancels smooth programmatic scrolling on a snap container and
// re-snaps to the current snap area, so pages using scroll-snap would never
// leave the active chapter. Disabling snap for the duration of the animation
// and restoring it once scrolling settles fixes this; on pages without
// scroll-snap this adds nothing.
//
// options.center: scroll so the target sits vertically centered in the
// viewport. Targets taller than the viewport instead align near the top with
// enough offset to clear the fixed header. Without the option, the browser's
// own scrollIntoView runs, which respects the target's scroll-margin-top.

const HEADER_CLEARANCE = 120;

type Options = {
  center?: boolean;
};

function startScroll(target: Element, options?: Options) {
  if (!options?.center) {
    target.scrollIntoView({ behavior: 'smooth' });
    return;
  }

  const rect = target.getBoundingClientRect();
  const offset = Math.max((window.innerHeight - rect.height) / 2, HEADER_CLEARANCE);
  const top = Math.max(rect.top + window.scrollY - offset, 0);
  window.scrollTo({ top, behavior: 'smooth' });
}

export default function smoothScrollTo(target: Element, options?: Options) {
  const html = document.documentElement;

  const hadSnap = html.style.scrollSnapType || '';

  const hasDocumentSnap = getComputedStyle(html).scrollSnapType !== 'none';

  if (!hasDocumentSnap) {
    startScroll(target, options);
    return;
  }

  html.style.scrollSnapType = 'none';

  let settleTimer: ReturnType<typeof setTimeout>;

  const restore = () => {
    window.removeEventListener('scroll', onScroll);
    html.style.scrollSnapType = hadSnap;
  };

  const onScroll = () => {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(restore, 150);
  };

  window.addEventListener('scroll', onScroll);
  onScroll();

  startScroll(target, options);
}
